// ---------------------------------------------------------------------------
// Authentication facade. Holds the shared auth store (current user + status) and
// the token provider the sync transport uses, delegating the provider-specific
// work to MSAL in the browser/dev build (browserAuth) or the native loopback +
// PKCE flow in the Tauri desktop build (desktopAuth). Sign-up and sign-in share
// one CIAM user flow — the hosted page offers "Create account", and signUp()
// jumps straight to it.
// ---------------------------------------------------------------------------

import { useSyncExternalStore } from 'react';
import { isTauri } from '../ai/tauri';

export interface AuthUser {
  id: string;
  email: string | null;
  name: string | null;
}

export type AuthState = 'initializing' | 'signed-out' | 'signed-in';

export interface AuthProvider {
  /** Restore any persisted session; returns the user or null. */
  init(): Promise<AuthUser | null>;
  signIn(): Promise<AuthUser>;
  signUp(): Promise<AuthUser>;
  signOut(): Promise<void>;
  /** Bearer token for API calls, refreshing as needed; null when signed out. */
  getAccessToken(): Promise<string | null>;
}

interface AuthStore {
  state: AuthState;
  user: AuthUser | null;
  error: string | null;
}

let store: AuthStore = { state: 'initializing', user: null, error: null };
const listeners = new Set<() => void>();

function emit(patch: Partial<AuthStore>) {
  store = { ...store, ...patch };
  for (const l of listeners) l();
}

export function getAuth(): AuthStore {
  return store;
}
export function useAuth(): AuthStore {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    getAuth,
    getAuth,
  );
}

// The provider modules are imported lazily so the desktop build never pulls in
// MSAL and the browser build never references Tauri APIs.
let providerPromise: Promise<AuthProvider> | null = null;
function provider(): Promise<AuthProvider> {
  if (!providerPromise) {
    providerPromise = isTauri()
      ? import('./desktopAuth').then((m) => m.desktopAuth)
      : import('./browserAuth').then((m) => m.browserAuth);
  }
  return providerPromise;
}

let initPromise: Promise<void> | null = null;

export function initAuth(): Promise<void> {
  if (initPromise) return initPromise;
  initPromise = (async () => {
    try {
      const user = await (await provider()).init();
      emit(user ? { state: 'signed-in', user } : { state: 'signed-out', user: null });
    } catch {
      emit({ state: 'signed-out', user: null });
    }
  })();
  return initPromise;
}

export async function signIn(): Promise<AuthUser> {
  const user = await (await provider()).signIn();
  emit({ state: 'signed-in', user, error: null });
  return user;
}

export async function signUp(): Promise<AuthUser> {
  const user = await (await provider()).signUp();
  emit({ state: 'signed-in', user, error: null });
  return user;
}

export async function signOut(): Promise<void> {
  emit({ state: 'signed-out', user: null });
  try {
    await (await provider()).signOut();
  } catch {
    /* local state is already cleared above */
  }
}

/**
 * Called by a provider when a restored session turns out to be permanently dead
 * (e.g. the refresh token expired or was revoked), so the UI flips to
 * signed-out instead of showing a signed-in account whose sync silently fails.
 */
export function sessionExpired(): void {
  if (store.state === 'signed-in') emit({ state: 'signed-out', user: null });
}

export async function getAccessToken(): Promise<string | null> {
  try {
    return await (await provider()).getAccessToken();
  } catch {
    return null;
  }
}
